/**
 * PrinterManagementPage — MADE-275 / MADE-276
 *
 * Admin UI for managing 3D printers (list, add, edit, deactivate/reactivate).
 * MADE-276: Print History tab wired — fetches /api/madebb/machines/:id/history.
 * Stubbed detail panel tabs for MADE-277 (Maintenance), MADE-278 (Stats).
 *
 * Route:  /madebb/printers
 * API:    GET  /api/madebb/machines/all
 *         GET  /api/madebb/machines/:id/history
 *         POST /api/madebb/machines
 *         PATCH /api/madebb/machines/:id
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayCircle as PlayCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { authFetch } from '../utils/authFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// PrinterStats — MADE-278
// ---------------------------------------------------------------------------

interface PrinterStats {
  total_jobs: number;
  active_jobs: number;
  completed_jobs: number;
  cancelled_jobs: number;
  blocked_jobs: number;
  completion_rate: number | null;  // 0–1 float, or null if no non-cancelled jobs
  last_assigned_at: string | null; // ISO timestamp or null
}

/** Manufacturing order row returned by GET /api/madebb/machines/:id/history */
interface PrintHistoryRecord {
  id: string;
  order_number: string;
  material_name: string | null;
  description: string | null;
  quantity: number;
  unit: string;
  status: string;
  assigned_at: string;
  completed_at: string | null;
}

interface PrintHistoryResponse {
  history: PrintHistoryRecord[];
  total: number;
}

interface Printer {
  id: string;
  name: string;
  model: string | null;
  serial_number: string | null;
  location: string;
  description: string | null;
  is_default: boolean;
  active: boolean;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Status chip colour map — matches ManufacturingHub KanbanBoard
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  received:       { bg: '#1976d2', label: 'Received' },
  incomplete:     { bg: '#ed6c02', label: 'Incomplete' },
  in_production:  { bg: '#9c27b0', label: 'In Production' },
  quality_check:  { bg: '#0288d1', label: 'Quality Check' },
  pack_and_ship:  { bg: '#2e7d32', label: 'Pack & Ship' },
  completed:      { bg: '#607d8b', label: 'Completed' },
  cancelled:      { bg: '#d32f2f', label: 'Cancelled' },
  blocked:        { bg: '#d32f2f', label: 'Blocked' },
};

/** Format an ISO timestamp as "Aug 13, 2026" (full datetime-aware) */
const formatDateFromISO = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** Normalise a pg DATE value (may be a Date object or ISO string) to YYYY-MM-DD */
const normalizeDateStr = (d: string | Date): string => {
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  if (typeof d === 'string' && d.length > 10) return d.slice(0, 10);
  return d as string;
};

/** Format a date-only string as "Aug 13, 2026" (adds T00:00:00 to avoid UTC shift) */
const formatDate = (iso: string | Date): string =>
  new Date(`${normalizeDateStr(iso)}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** Format a date string as "Aug 13, 2026, 3:45 PM" */
const formatDateTime = (iso: string): string => {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// ---------------------------------------------------------------------------
// PrinterStatsTab — MADE-278
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: string;
  loading: boolean;
  testId: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, loading, testId }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={32} />
          ) : (
            <Typography variant="h5" fontWeight={700} data-testid={testId}>
              {value}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

interface PrinterStatsTabProps {
  printerId: string;
}

const PrinterStatsTab: React.FC<PrinterStatsTabProps> = ({ printerId }) => {
  const [stats, setStats] = useState<PrinterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(`/api/madebb/machines/${printerId}/stats`);
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();
        if (!cancelled) setStats(data.stats ?? data);
      } catch (err: unknown) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [printerId]);

  const completionRateDisplay =
    stats && stats.completion_rate !== null
      ? `${(stats.completion_rate * 100).toFixed(1)}%`
      : '—';

  const lastAssignedDisplay =
    stats && stats.last_assigned_at
      ? formatDateFromISO(stats.last_assigned_at)
      : '—';

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }} data-testid="stats-error">
        {error}
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Total Jobs"
          value={stats?.total_jobs ?? 0}
          icon={<AssignmentIcon />}
          color="#1565c0"
          loading={loading}
          testId="stats-total-jobs"
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Active Jobs"
          value={stats?.active_jobs ?? 0}
          icon={<PlayCircleIcon />}
          color="#2e7d32"
          loading={loading}
          testId="stats-active-jobs"
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Completed Jobs"
          value={stats?.completed_jobs ?? 0}
          icon={<CheckCircleIcon />}
          color="#1b5e20"
          loading={loading}
          testId="stats-completed-jobs"
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Cancelled Jobs"
          value={stats?.cancelled_jobs ?? 0}
          icon={<CancelIcon />}
          color="#c62828"
          loading={loading}
          testId="stats-cancelled-jobs"
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Completion Rate"
          value={stats ? completionRateDisplay : '—'}
          icon={<TrendingUpIcon />}
          color="#e65100"
          loading={loading}
          testId="stats-completion-rate"
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <StatCard
          label="Last Assigned"
          value={stats ? lastAssignedDisplay : '—'}
          icon={<ScheduleIcon />}
          color="#616161"
          loading={loading}
          testId="stats-last-assigned"
        />
      </Grid>
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// MaintenanceLogTab — MADE-277
// ---------------------------------------------------------------------------

type MaintenanceEventType = 'routine' | 'repair' | 'calibration' | 'upgrade' | 'other';

interface MaintenanceEntry {
  id: number;
  machine_id: string;
  event_date: string;       // ISO date string "YYYY-MM-DD"
  event_type: MaintenanceEventType;
  technician: string | null;
  hours_down: number | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const MAINTENANCE_TYPE_COLORS: Record<MaintenanceEventType, string> = {
  routine:     '#1565c0',
  repair:      '#c62828',
  calibration: '#6a1b9a',
  upgrade:     '#00695c',
  other:       '#616161',
};

const MAINTENANCE_TYPE_LABELS: Record<MaintenanceEventType, string> = {
  routine:     'Routine',
  repair:      'Repair',
  calibration: 'Calibration',
  upgrade:     'Upgrade',
  other:       'Other',
};

const todayISO = (): string => new Date().toISOString().slice(0, 10);

interface MaintenanceEntryForm {
  event_date: string;
  event_type: MaintenanceEventType;
  technician: string;
  hours_down: string;
  notes: string;
}

const EMPTY_MAINTENANCE_FORM: MaintenanceEntryForm = {
  event_date: '',
  event_type: 'routine',
  technician: '',
  hours_down: '',
  notes: '',
};

interface MaintenanceLogTabProps {
  printerId: string;
}

const MaintenanceLogTab: React.FC<MaintenanceLogTabProps> = ({ printerId }) => {
  const [entries, setEntries] = useState<MaintenanceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add/Edit dialog
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<MaintenanceEntry | null>(null);
  const [form, setForm] = useState<MaintenanceEntryForm>(EMPTY_MAINTENANCE_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<MaintenanceEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [mSnackOpen, setMSnackOpen] = useState(false);
  const [mSnackMessage, setMSnackMessage] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/api/madebb/machines/${printerId}/maintenance`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setEntries(data.entries ?? data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load maintenance log');
    } finally {
      setLoading(false);
    }
  }, [printerId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openAddDialog = () => {
    setEditingEntry(null);
    setForm({ ...EMPTY_MAINTENANCE_FORM, event_date: todayISO() });
    setFormError(null);
    setEntryDialogOpen(true);
  };

  const openEditDialog = (entry: MaintenanceEntry) => {
    setEditingEntry(entry);
    setForm({
      event_date: normalizeDateStr(entry.event_date),
      event_type: entry.event_type,
      technician: entry.technician ?? '',
      hours_down: entry.hours_down != null ? String(entry.hours_down) : '',
      notes: entry.notes ?? '',
    });
    setFormError(null);
    setEntryDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    const payload = {
      event_date: form.event_date,
      event_type: form.event_type,
      technician: form.technician.trim() || null,
      hours_down: form.hours_down !== '' ? parseFloat(form.hours_down) : null,
      notes: form.notes.trim() || null,
    };
    try {
      const url = editingEntry
        ? `/api/madebb/machines/${printerId}/maintenance/${editingEntry.id}`
        : `/api/madebb/machines/${printerId}/maintenance`;
      const method = editingEntry ? 'PATCH' : 'POST';
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: string; message?: string }).error ??
          (data as { error?: string; message?: string }).message ??
          `Server returned ${res.status}`
        );
      }
      await fetchEntries();
      setEntryDialogOpen(false);
      setMSnackMessage('Entry saved');
      setMSnackOpen(true);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEntry) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `/api/madebb/machines/${printerId}/maintenance/${deletingEntry.id}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      await fetchEntries();
      setDeleteDialogOpen(false);
      setMSnackMessage('Entry deleted');
      setMSnackOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={openAddDialog}
          data-testid="maintenance-add-btn"
        >
          Add Entry
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Typography color="text.secondary" data-testid="maintenance-empty">
          No maintenance events logged for this printer.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="maintenance-table">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Hours Down</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <Typography variant="body2" whiteSpace="nowrap">
                      {formatDate(entry.event_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={MAINTENANCE_TYPE_LABELS[entry.event_type] ?? entry.event_type}
                      size="small"
                      sx={{
                        bgcolor: MAINTENANCE_TYPE_COLORS[entry.event_type] ?? '#616161',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.technician ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" whiteSpace="nowrap">
                      {entry.hours_down != null ? `${entry.hours_down} h` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      title={entry.notes ?? ''}
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.notes
                        ? entry.notes.length > 80
                          ? `${entry.notes.slice(0, 80)}…`
                          : entry.notes
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(entry)}
                        aria-label={`Edit maintenance entry ${entry.id}`}
                        data-testid={`maintenance-edit-btn-${entry.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeletingEntry(entry);
                          setDeleteDialogOpen(true);
                        }}
                        aria-label={`Delete maintenance entry ${entry.id}`}
                        data-testid={`maintenance-delete-btn-${entry.id}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit dialog */}
      <Dialog
        open={entryDialogOpen}
        onClose={() => setEntryDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        data-testid="maintenance-entry-dialog"
      >
        <DialogTitle>
          {editingEntry ? 'Edit Maintenance Entry' : 'Add Maintenance Entry'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && (
              <Alert severity="error" data-testid="maintenance-form-error">
                {formError}
              </Alert>
            )}
            <TextField
              label="Date"
              type="date"
              value={form.event_date}
              onChange={(e) => setForm((prev) => ({ ...prev, event_date: e.target.value }))}
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'maintenance-date-input' }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel id="maintenance-type-label">Type</InputLabel>
              <Select
                labelId="maintenance-type-label"
                label="Type"
                value={form.event_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    event_type: e.target.value as MaintenanceEventType,
                  }))
                }
                inputProps={{ 'data-testid': 'maintenance-type-select' }}
              >
                <MenuItem value="routine">Routine</MenuItem>
                <MenuItem value="repair">Repair</MenuItem>
                <MenuItem value="calibration">Calibration</MenuItem>
                <MenuItem value="upgrade">Upgrade</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Technician"
              value={form.technician}
              onChange={(e) => setForm((prev) => ({ ...prev, technician: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{ 'data-testid': 'maintenance-technician-input' }}
            />
            <TextField
              label="Hours Down"
              type="number"
              value={form.hours_down}
              onChange={(e) => setForm((prev) => ({ ...prev, hours_down: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{ step: 0.5, min: 0, 'data-testid': 'maintenance-hours-input' }}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={3}
              fullWidth
              size="small"
              inputProps={{ 'data-testid': 'maintenance-notes-input' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntryDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            data-testid="maintenance-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        data-testid="maintenance-delete-dialog"
      >
        <DialogTitle>Delete maintenance entry?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this maintenance entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            data-testid="maintenance-delete-confirm"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={mSnackOpen}
        autoHideDuration={4000}
        onClose={() => setMSnackOpen(false)}
        message={mSnackMessage}
        data-testid="maintenance-snackbar"
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PrintHistoryTab — MADE-276
// ---------------------------------------------------------------------------

interface PrintHistoryTabProps {
  printerId: string;
}

const PrintHistoryTab: React.FC<PrintHistoryTabProps> = ({ printerId }) => {
  const [history, setHistory] = useState<PrintHistoryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the first page on mount (or when printerId changes)
  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      setHistory([]);
      setTotal(0);
      try {
        const res = await authFetch(
          `/api/madebb/machines/${printerId}/history?limit=50&offset=0`
        );
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data: PrintHistoryResponse = await res.json();
        if (!cancelled) {
          setHistory(data.history ?? []);
          setTotal(data.total ?? 0);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load print history'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [printerId]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await authFetch(
        `/api/madebb/machines/${printerId}/history?limit=50&offset=${history.length}`
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data: PrintHistoryResponse = await res.json();
      setHistory((prev) => [...prev, ...(data.history ?? [])]);
      setTotal(data.total ?? total);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to load more history'
      );
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }} data-testid="print-history-error">
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Typography
        color="text.secondary"
        sx={{ py: 3 }}
        data-testid="print-history-empty"
      >
        No manufacturing orders have been assigned to this printer yet.
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" data-testid="print-history-table">
          <TableHead>
            <TableRow>
              <TableCell>MO #</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Completed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((row) => {
              const statusInfo = STATUS_COLORS[row.status];
              const productName = row.material_name ?? row.description ?? '—';
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                    >
                      {row.order_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{productName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" whiteSpace="nowrap">
                      {row.quantity} {row.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusInfo?.label ?? row.status}
                      size="small"
                      sx={{
                        bgcolor: statusInfo?.bg ?? 'grey.400',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" whiteSpace="nowrap">
                      {formatDateTime(row.assigned_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" whiteSpace="nowrap" color="text.secondary">
                      {row.completed_at ? formatDateTime(row.completed_at) : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {total > history.length && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-testid="print-history-load-more"
          >
            {loadingMore ? 'Loading…' : `Load more (${total - history.length} remaining)`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PrinterFormDrawer — Add / Edit
// ---------------------------------------------------------------------------

interface PrinterFormDrawerProps {
  open: boolean;
  printer: Printer | null; // null = add mode
  onClose: () => void;
  onRefresh: () => void;
  onSuccess: (msg: string) => void;
}

const EMPTY_FORM = {
  name: '',
  model: '',
  serial_number: '',
  location: 'print_shop',
  description: '',
  is_default: false,
  active: true,
};

const PrinterFormDrawer: React.FC<PrinterFormDrawerProps> = ({
  open,
  printer,
  onClose,
  onRefresh,
  onSuccess,
}) => {
  const isEdit = printer !== null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [nameError, setNameError] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate when editing or reset when adding
  useEffect(() => {
    if (open) {
      if (printer) {
        setForm({
          name: printer.name,
          model: printer.model ?? '',
          serial_number: printer.serial_number ?? '',
          location: printer.location,
          description: printer.description ?? '',
          is_default: printer.is_default,
          active: printer.active,
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setNameError(false);
      setLocationError(false);
      setError(null);
    }
  }, [open, printer]);

  const handleChange = (field: keyof typeof EMPTY_FORM, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && typeof value === 'string' && value.trim()) setNameError(false);
    if (field === 'location' && typeof value === 'string' && value.trim()) setLocationError(false);
  };

  const handleSave = async () => {
    let valid = true;
    if (!form.name.trim()) { setNameError(true); valid = false; }
    if (!form.location.trim()) { setLocationError(true); valid = false; }
    if (!valid) return;

    setSaving(true);
    setError(null);

    const payload = {
      name: form.name.trim(),
      model: form.model.trim() || null,
      serial_number: form.serial_number.trim() || null,
      location: form.location.trim(),
      description: form.description.trim() || null,
      is_default: form.is_default,
      ...(isEdit ? { active: form.active } : {}),
    };

    try {
      const url = isEdit
        ? `/api/madebb/machines/${printer!.id}`
        : '/api/madebb/machines';
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: string; message?: string }).error ??
          (data as { error?: string; message?: string }).message ??
          `Server returned ${res.status}`
        );
      }

      onRefresh();
      onSuccess(isEdit ? 'Printer updated.' : 'Printer added.');
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save printer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 480 }, p: 3 },
        'data-testid': 'printer-drawer',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          {isEdit ? 'Edit Printer' : 'Add Printer'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close drawer">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={2}>
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          error={nameError}
          helperText={nameError ? 'Name is required' : undefined}
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'printer-name-input' }}
        />

        <TextField
          label="Model"
          value={form.model}
          onChange={(e) => handleChange('model', e.target.value)}
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'printer-model-input' }}
        />

        <TextField
          label="Serial Number"
          value={form.serial_number}
          onChange={(e) => handleChange('serial_number', e.target.value)}
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'printer-serial-input' }}
        />

        <TextField
          label="Location"
          value={form.location}
          onChange={(e) => handleChange('location', e.target.value)}
          required
          error={locationError}
          helperText={locationError ? 'Location is required' : undefined}
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'printer-location-input' }}
        />

        <TextField
          label="Description"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
          inputProps={{ 'data-testid': 'printer-description-input' }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.is_default}
              onChange={(e) => handleChange('is_default', e.target.checked)}
              data-testid="printer-default-switch"
            />
          }
          label="Set as Default"
        />

        {/* Active toggle — only shown on edit */}
        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                data-testid="printer-active-switch"
              />
            }
            label="Active"
          />
        )}

        {error && (
          <Alert severity="error" data-testid="printer-form-error">
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            data-testid="printer-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// PrinterDetailPanel — Overview + stub tabs for MADE-276/277/278
// ---------------------------------------------------------------------------

interface PrinterDetailPanelProps {
  open: boolean;
  printer: Printer | null;
  onClose: () => void;
}

const PrinterDetailPanel: React.FC<PrinterDetailPanelProps> = ({ open, printer, onClose }) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) setTab(0);
  }, [open, printer]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 520 }, p: 3 },
        'data-testid': 'printer-detail-panel',
      }}
    >
      {printer && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{printer.name}</Typography>
              {printer.model && (
                <Typography variant="body2" color="text.secondary">{printer.model}</Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="Close detail panel">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            aria-label="Printer detail tabs"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Overview" />
            <Tab label="Print History" />
            <Tab label="Maintenance" />
            <Tab label="Stats" />
          </Tabs>

          {/* Tab 0: Overview */}
          {tab === 0 && (
            <Stack spacing={1.5}>
              <DetailRow label="Name" value={printer.name} />
              <DetailRow label="Model" value={printer.model ?? '—'} />
              <DetailRow label="Serial Number" value={printer.serial_number ?? '—'} />
              <DetailRow label="Location" value={printer.location} />
              <DetailRow label="Description" value={printer.description ?? '—'} />
              <DetailRow
                label="Status"
                value={
                  <Chip
                    label={printer.active ? 'Active' : 'Inactive'}
                    color={printer.active ? 'success' : 'default'}
                    size="small"
                  />
                }
              />
              <DetailRow
                label="Default"
                value={printer.is_default ? <Chip label="Default" color="primary" size="small" /> : '—'}
              />
              <DetailRow
                label="Created"
                value={new Date(printer.created_at).toLocaleDateString()}
              />
            </Stack>
          )}

          {/* Tab 1: Print History — MADE-276 */}
          {tab === 1 && (
            <PrintHistoryTab printerId={printer.id} />
          )}

          {/* Tab 2: Maintenance — MADE-277 */}
          {tab === 2 && (
            <MaintenanceLogTab printerId={printer.id} />
          )}

          {/* Tab 3: Stats — MADE-278 */}
          {tab === 3 && (
            <PrinterStatsTab printerId={printer.id} />
          )}
        </Box>
      )}
    </Drawer>
  );
};

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {value}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// DeactivateDialog
// ---------------------------------------------------------------------------

interface DeactivateDialogProps {
  open: boolean;
  printer: Printer | null;
  onClose: () => void;
  onConfirm: () => void;
}

const DeactivateDialog: React.FC<DeactivateDialogProps> = ({ open, printer, onClose, onConfirm }) => {
  if (!printer) return null;
  const isActive = printer.active;

  return (
    <Dialog open={open} onClose={onClose} data-testid="deactivate-dialog">
      <DialogTitle>{isActive ? `Deactivate ${printer.name}?` : `Reactivate ${printer.name}?`}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isActive
            ? `It will no longer appear in the production assignment dropdown.`
            : `It will become available again in the production assignment dropdown.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="dialog-cancel-btn">Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isActive ? 'error' : 'primary'}
          data-testid="dialog-confirm-btn"
        >
          {isActive ? 'Deactivate' : 'Reactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// PrinterManagementPage
// ---------------------------------------------------------------------------

export const PrinterManagementPage: React.FC = () => {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add/Edit drawer
  const [formOpen, setFormOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);

  // Detail panel
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPrinter, setDetailPrinter] = useState<Printer | null>(null);

  // Deactivate/reactivate dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPrinter, setDialogPrinter] = useState<Printer | null>(null);
  const [dialogSaving, setDialogSaving] = useState(false);

  // Snackbar
  const [snackMessage, setSnackMessage] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);

  const fetchPrinters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/madebb/machines/all');
      if (!res.ok) throw new Error(`Failed to load printers (${res.status})`);
      const data = await res.json();
      setPrinters(data.machines ?? data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load printers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  const handleAddClick = () => {
    setEditingPrinter(null);
    setFormOpen(true);
  };

  const handleEditClick = (printer: Printer, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingPrinter(printer);
    setFormOpen(true);
  };

  const handleRowClick = (printer: Printer) => {
    setDetailPrinter(printer);
    setDetailOpen(true);
  };

  const handleDeactivateClick = (printer: Printer, e: React.MouseEvent) => {
    e.stopPropagation();
    setDialogPrinter(printer);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    if (!dialogPrinter) return;
    setDialogSaving(true);
    try {
      const res = await authFetch(`/api/madebb/machines/${dialogPrinter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !dialogPrinter.active }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: string }).error ?? `Server returned ${res.status}`
        );
      }
      fetchPrinters();
      setSnackMessage(dialogPrinter.active ? 'Printer deactivated.' : 'Printer reactivated.');
      setSnackOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setDialogSaving(false);
      setDialogOpen(false);
    }
  };

  const handleSuccess = (msg: string) => {
    setSnackMessage(msg);
    setSnackOpen(true);
  };

  return (
    <Box data-testid="printer-management-page">
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Manage Printers
        </Typography>
        <Button
          variant="contained"
          onClick={handleAddClick}
          data-testid="add-printer-btn"
        >
          Add Printer
        </Button>
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="printer-error-alert">
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress data-testid="printer-loading-spinner" />
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && printers.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} data-testid="printer-empty-state">
          No printers registered. Click &lsquo;Add Printer&rsquo; to get started.
        </Typography>
      )}

      {/* Printer table */}
      {!loading && printers.length > 0 && (
        <Paper>
          <TableContainer>
            <Table data-testid="printer-list-table" aria-label="Printer list">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {printers.map((printer) => (
                  <TableRow
                    key={printer.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(printer)}
                    data-testid={`printer-row-${printer.id}`}
                  >
                    {/* Name + Default chip */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{printer.name}</Typography>
                        {printer.is_default && (
                          <Chip
                            label="Default"
                            color="primary"
                            size="small"
                            data-testid={`printer-default-chip-${printer.id}`}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Model */}
                    <TableCell>{printer.model ?? '—'}</TableCell>

                    {/* Location */}
                    <TableCell>{printer.location}</TableCell>

                    {/* Status chip */}
                    <TableCell>
                      <Chip
                        label={printer.active ? 'Active' : 'Inactive'}
                        color={printer.active ? 'success' : 'default'}
                        size="small"
                        data-testid={`printer-status-chip-${printer.id}`}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(printer, e)}
                          data-testid={`printer-edit-btn-${printer.id}`}
                          aria-label={`Edit ${printer.name}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Button
                          variant="outlined"
                          size="small"
                          color={printer.active ? 'error' : 'primary'}
                          onClick={(e) => handleDeactivateClick(printer, e)}
                          data-testid={`printer-toggle-btn-${printer.id}`}
                        >
                          {printer.active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit drawer */}
      <PrinterFormDrawer
        open={formOpen}
        printer={editingPrinter}
        onClose={() => setFormOpen(false)}
        onRefresh={fetchPrinters}
        onSuccess={handleSuccess}
      />

      {/* Detail panel */}
      <PrinterDetailPanel
        open={detailOpen}
        printer={detailPrinter}
        onClose={() => setDetailOpen(false)}
      />

      {/* Deactivate/Reactivate dialog */}
      <DeactivateDialog
        open={dialogOpen}
        printer={dialogPrinter}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDialogConfirm}
      />

      {/* Disable confirm btn while saving */}
      {dialogSaving && (
        <Box sx={{ position: 'fixed', bottom: 80, right: 24 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Success snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
        data-testid="printer-snackbar"
      />
    </Box>
  );
};
